import { useState } from 'react';
import { X, Save } from 'lucide-react';
import BookSearchSelect from './BookSearchSelect';

const FallbackBookModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    book_id: '',
    note: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!String(formData.book_id).trim()) {
      setError('Please search for and select a book.');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await onSuccess({
        book_id: parseInt(formData.book_id),
        note: formData.note.trim() || null
      });

      // Reset form and close
      setFormData({ book_id: '', note: '' });
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to create fallback record');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({ book_id: '', note: '' });
      setError(null);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-md">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-text-primary">Create Fallback Book</h2>
          <button onClick={handleClose} disabled={loading} className="p-2 hover:bg-gray-100 rounded-full disabled:opacity-50">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
              {error}
            </div>
          )}

          <BookSearchSelect
            label="Book"
            required
            disabled={loading}
            selectedId={formData.book_id}
            onSelect={(book) => setFormData(prev => ({ ...prev, book_id: book ? book.id : '' }))}
            helperText="Used when no age range has an active Book of the Week."
          />

          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Note</label>
            <textarea
              value={formData.note}
              onChange={(e) => setFormData(prev => ({ ...prev, note: e.target.value }))}
              placeholder="Optional note about this fallback selection..."
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300 h-20"
              disabled={loading}
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="px-4 py-2 border border-gray-200 text-text-secondary rounded hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary flex items-center space-x-2 disabled:opacity-50"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              <span>{loading ? 'Creating...' : 'Create Fallback'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FallbackBookModal;